/**
 * @fileoverview Health status models for application service monitoring
 * @description Defines health check results, service status, and incident tracking
 * @project AIEMD Platform - Resilience System
 * @created 2025-01-18
 */

/**
 * Overall health status levels
 */
export enum HealthLevel {
  /** All services operational */
  HEALTHY = 'healthy',

  /** Minor issues, degraded performance */
  DEGRADED = 'degraded',

  /** Major issues affecting functionality */
  UNHEALTHY = 'unhealthy',

  /** Cannot determine health status */
  UNKNOWN = 'unknown',
}

/**
 * Individual service status
 */
export enum ServiceStatus {
  /** Service is fully operational */
  OPERATIONAL = 'operational',

  /** Service experiencing minor issues */
  DEGRADED_PERFORMANCE = 'degraded_performance',

  /** Service experiencing partial outage */
  PARTIAL_OUTAGE = 'partial_outage',

  /** Service is completely down */
  MAJOR_OUTAGE = 'major_outage',

  /** Under planned maintenance */
  MAINTENANCE = 'maintenance',

  /** Status unknown or unreachable */
  UNKNOWN = 'unknown',
}

/**
 * Application services that can be monitored
 */
export enum MonitoredService {
  API_GATEWAY = 'api_gateway',
  AUTH = 'auth',
  DATABASE = 'database',
  STORAGE = 'storage',
  REALTIME = 'realtime',
  EDGE_FUNCTIONS = 'edge_functions',
  CONNECTION_POOLER = 'connection_pooler',
  MANAGEMENT_API = 'management_api',
  DASHBOARD = 'dashboard',
  ANALYTICS = 'analytics',
}

/**
 * Status of a single service
 */
export interface ServiceHealthStatus {
  /** Service identifier */
  service: MonitoredService;

  /** Human-readable service name */
  name: string;

  /** Current status */
  status: ServiceStatus;

  /** Human-readable status message */
  message?: string;

  /** When this status was last updated */
  lastUpdated: number;

  /** Uptime percentage (if available) */
  uptimePercentage?: number;

  /** Current incident ID if any */
  incidentId?: string;

  /** Response time in ms (if available) */
  responseTime?: number;
}

/**
 * Project-specific health check result
 */
export interface ProjectHealth {
  /** Overall project health level */
  level: HealthLevel;

  /** Status of individual services */
  services: ServiceHealthStatus[];

  /** Whether project is accessible */
  isAccessible: boolean;

  /** Project reference ID */
  projectRef: string;

  /** When health was checked */
  checkedAt: number;

  /** Average response time across services */
  avgResponseTime?: number;

  /** Any error messages from health check */
  error?: string;
}

/**
 * Platform-wide status from the platform status feed
 */
export interface PlatformStatus {
  /** Overall platform health */
  level: HealthLevel;

  /** Status of platform services */
  services: ServiceHealthStatus[];

  /** Active incidents */
  activeIncidents: IncidentInfo[];

  /** Recent resolved incidents */
  recentIncidents: IncidentInfo[];

  /** When status was fetched */
  fetchedAt: number;

  /** Next scheduled maintenance */
  scheduledMaintenance?: MaintenanceInfo[];
}

/**
 * Incident information
 */
export interface IncidentInfo {
  /** Unique incident ID */
  id: string;

  /** Incident title */
  title: string;

  /** Detailed description */
  description: string;

  /** Current incident status */
  status: IncidentStatus;

  /** Severity level */
  impact: IncidentImpact;

  /** Affected services */
  affectedServices: MonitoredService[];

  /** When incident started */
  startedAt: number;

  /** When incident was resolved (if applicable) */
  resolvedAt?: number;

  /** Updates on the incident */
  updates: IncidentUpdate[];

  /** Link to status page */
  url?: string;
}

/**
 * Incident status
 */
export enum IncidentStatus {
  INVESTIGATING = 'investigating',
  IDENTIFIED = 'identified',
  MONITORING = 'monitoring',
  RESOLVED = 'resolved',
  POSTMORTEM = 'postmortem',
}

/**
 * Incident impact level
 */
export enum IncidentImpact {
  NONE = 'none',
  MINOR = 'minor',
  MAJOR = 'major',
  CRITICAL = 'critical',
}

/**
 * Update on an incident
 */
export interface IncidentUpdate {
  /** Update timestamp */
  timestamp: number;

  /** Update status */
  status: IncidentStatus;

  /** Update message */
  message: string;

  /** Update author */
  author?: string;
}

/**
 * Scheduled maintenance information
 */
export interface MaintenanceInfo {
  /** Maintenance ID */
  id: string;

  /** Title */
  title: string;

  /** Description */
  description: string;

  /** Affected services */
  affectedServices: MonitoredService[];

  /** Scheduled start time */
  scheduledFor: number;

  /** Expected duration in minutes */
  durationMinutes: number;

  /** Link to announcement */
  url?: string;
}

/**
 * Combined health status (project + platform)
 */
export interface CombinedHealthStatus {
  /** Project-specific health */
  project: ProjectHealth;

  /** Platform-wide status */
  platform: PlatformStatus;

  /** Overall assessment */
  overall: HealthLevel;

  /** Whether issues are local or platform-wide */
  issueScope: 'project' | 'platform' | 'both' | 'none';

  /** Recommended action for users */
  recommendation: string;

  /** When assessment was made */
  assessedAt: number;
}

/**
 * Health check configuration
 */
export interface HealthCheckConfig {
  /** How often to check health (ms) */
  checkInterval: number;

  /** Timeout for health checks (ms) */
  timeout: number;

  /** Cache duration for health results (ms) */
  cacheDuration: number;

  /** Whether to check platform status */
  checkPlatformStatus: boolean;

  /** Whether to check project metrics */
  checkProjectMetrics: boolean;

  /** Project metrics endpoint */
  metricsEndpoint?: string;

  /** Platform status feed URL */
  statusFeedUrl: string;
}

/**
 * Helper functions for health status
 */

/**
 * Determines overall health level from service statuses
 */
export function calculateOverallHealth(
  services: ServiceHealthStatus[]
): HealthLevel {
  if (services.length === 0) return HealthLevel.UNKNOWN;

  const hasMajor = services.some(
    s => s.status === ServiceStatus.MAJOR_OUTAGE
  );
  const hasPartial = services.some(
    s => s.status === ServiceStatus.PARTIAL_OUTAGE
  );
  const hasDegraded = services.some(
    s => s.status === ServiceStatus.DEGRADED_PERFORMANCE
  );

  if (hasMajor) return HealthLevel.UNHEALTHY;
  if (hasPartial) return HealthLevel.DEGRADED;
  if (hasDegraded) return HealthLevel.DEGRADED;

  return HealthLevel.HEALTHY;
}

/**
 * Check if a specific service is affected
 */
export function isServiceAffected(
  service: MonitoredService,
  status: ProjectHealth | PlatformStatus
): boolean {
  const serviceStatus = status.services.find(s => s.service === service);
  return (
    serviceStatus !== undefined &&
    serviceStatus.status !== ServiceStatus.OPERATIONAL
  );
}

/**
 * Get user-friendly health message
 */
export function getHealthMessage(level: HealthLevel): string {
  switch (level) {
    case HealthLevel.HEALTHY:
      return 'All systems operational';
    case HealthLevel.DEGRADED:
      return 'Experiencing minor issues';
    case HealthLevel.UNHEALTHY:
      return 'Major service disruption';
    case HealthLevel.UNKNOWN:
      return 'Unable to determine status';
  }
}

/**
 * Get CSS class for health level
 */
export function getHealthColorClass(level: HealthLevel): string {
  switch (level) {
    case HealthLevel.HEALTHY:
      return 'text-success-600';
    case HealthLevel.DEGRADED:
      return 'text-warning-600';
    case HealthLevel.UNHEALTHY:
      return 'text-error-600';
    case HealthLevel.UNKNOWN:
      return 'text-neutral-500';
  }
}

/**
 * Format incident duration
 */
export function formatIncidentDuration(incident: IncidentInfo): string {
  const start = incident.startedAt;
  const end = incident.resolvedAt || Date.now();
  const durationMs = end - start;

  const minutes = Math.floor(durationMs / 60000);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }

  return `${minutes}m`;
}

/**
 * Check if incident is recent (within last 24 hours)
 */
export function isRecentIncident(incident: IncidentInfo): boolean {
  const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
  return incident.startedAt > oneDayAgo;
}
